import React, { useState, useEffect } from 'react';
import API from '../../api';

/**
 * Admin KPI Dashboard Component
 * Shows key performance indicators with animated counters
 */
const AdminKPIDashboard = () => {
  const [stats, setStats] = useState({
    totalElections: 0,
    activeElections: 0,
    totalRegisteredVoters: 0,
    totalVotesCast: 0,
    pendingBlockchainSync: 0,
    faceVerifiedVoters: 0,
    systemAlerts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const res = await API.get('/admin/stats');
      if (res.data.success && res.data.stats) {
        // Ensure all stats have default values
        setStats({
          totalElections: res.data.stats.totalElections || 0,
          activeElections: res.data.stats.activeElections || 0,
          totalRegisteredVoters: res.data.stats.totalRegisteredVoters || 0,
          totalVotesCast: res.data.stats.totalVotesCast || 0,
          pendingBlockchainSync: res.data.stats.pendingBlockchainSync || 0,
          faceVerifiedVoters: res.data.stats.faceVerifiedVoters || 0,
          systemAlerts: res.data.stats.systemAlerts || 0
        });
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch admin stats:', error.response?.data || error.message);
      setLoading(false);
      // Keep default stats on error
    }
  };

  const KPICard = ({ title, value, icon, gradient, shadowColor, trend }) => {
    return (
      <div className={`backdrop-blur-xl bg-white/5 p-6 rounded-2xl border border-white/10 shadow-${shadowColor} hover:scale-105 transition-all duration-300`}>
        <div className="flex items-center justify-between mb-4">
          <div className={`w-14 h-14 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-lg`}>
            {icon}
          </div>
          {trend && (
            <div className="flex items-center text-green-400 text-sm">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd"/>
              </svg>
              {trend}
            </div>
          )}
        </div>
        
        <div className="space-y-2">
          <p className="text-gray-400 text-sm font-medium">{title}</p>
          <p className="text-4xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            {loading ? '...' : (value !== undefined && value !== null ? String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '0')}
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 mb-8">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-neon-purple via-neon-pink to-neon-aqua bg-clip-text text-transparent">
          📊 System Overview
        </h2>
        <button
          onClick={fetchStats}
          className="px-4 py-2 bg-white/5 border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 transition-all"
          title="Refresh stats"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Elections"
          value={stats.totalElections}
          gradient="from-neon-purple to-neon-pink"
          shadowColor="neon-purple"
          icon={
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
          }
        />

        <KPICard
          title="Active Elections"
          value={stats.activeElections}
          gradient="from-green-500 to-emerald-500"
          shadowColor="green-500"
          icon={
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
            </svg>
          }
        />

        <KPICard
          title="Registered Voters"
          value={stats.totalRegisteredVoters}
          gradient="from-neon-blue to-neon-aqua"
          shadowColor="neon-blue"
          icon={
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
            </svg>
          }
        />

        <KPICard
          title="Total Votes Cast"
          value={stats.totalVotesCast}
          gradient="from-neon-pink to-neon-purple"
          shadowColor="neon-pink"
          icon={
            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
              <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm9.707 5.707a1 1 0 00-1.414-1.414L9 12.586l-1.293-1.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
            </svg>
          }
        />

        <KPICard
          title="Face Verified"
          value={stats.faceVerifiedVoters}
          gradient="from-yellow-500 to-orange-500"
          shadowColor="yellow-500"
          icon={
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
          }
        />

        <KPICard
          title="Pending Blockchain"
          value={stats.pendingBlockchainSync}
          gradient="from-orange-500 to-red-500"
          shadowColor="orange-500"
          icon={
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          }
        />

        <KPICard
          title="System Alerts"
          value={stats.systemAlerts}
          gradient={stats.systemAlerts > 0 ? "from-red-500 to-pink-500" : "from-gray-600 to-gray-500"}
          shadowColor={stats.systemAlerts > 0 ? "red-500" : "gray-500"}
          icon={
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
            </svg>
          }
        />

        <KPICard
          title="Voter Turnout"
          value={stats.totalVotesCast > 0 && stats.totalRegisteredVoters > 0 
            ? `${((stats.totalVotesCast / stats.totalRegisteredVoters) * 100).toFixed(1)}%` 
            : '0%'}
          gradient="from-indigo-500 to-purple-500"
          shadowColor="indigo-500"
          icon={
            <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
            </svg>
          }
        />
      </div>
    </div>
  );
};

export default AdminKPIDashboard;
